import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DatePicker from '@mui/lab/DatePicker';

import { DialogTitleWithClose, RangeSlider, SelectWithSearch, GroupedSelect } from 'ui';
import { currencies } from 'shared/constants/currencies';
import { creditRatingRangeValues } from 'shared/constants/creditRatingRangeValues';
import { useGroupedAutocompleteStyles } from 'shared/styles/groupedAutocompleteStyles/groupedAutocompleteStyles';
import { flattenIndustries } from 'shared/parsers';
import { useRegions } from 'shared/hooks';
import { fetchCoveredBoundsSeniorityRatings } from 'services/coveredBonds/coveredBondsServices';

const useStyles = makeStyles(theme => ({
  datePicker: {
    marginTop: theme.spacing(1),
  },
}));

const currenciesOptions = currencies.map(({ code, name }) => ({ value: code, label: name }));

const couponRangeSliderValues = Array(16)
  .fill(null)
  .map((_, index) => ({ label: `${index}%`, value: index }));

const InstrumentsFiltersModal = ({
  open,
  onClose,
  onSubmit,
  searchQuery,
  initialFilter,
  coveredBond,
  countries,
  lockCountry,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const groupedAutocompleteClasses = useGroupedAutocompleteStyles();
  const { data: industriesData } = useSelector(({ shared }) => shared.industries);
  const regions = useRegions(searchQuery);

  const ratingTypes = [
    { value: 'PUBLIC', label: t('Public') },
    { value: 'ON_REQUEST', label: t('Subscription') },
  ];

  const issueVolumeRangeSliderValues = [100, 200, 300, 400, 500, 600, 700, 800, 900].map(x => ({
    label: `${x} ${t('mil')}`,
    value: x * 1000000,
  }));

  issueVolumeRangeSliderValues.push({
    label: `1 ${t('bil')}`,
    value: 1000000000,
  });

  const [values, setValues] = useState({
    seniorityTypes: [],
    countries: initialFilter.countries ? initialFilter.countries : [],
    industries: [],
    ratingType: null,
    ratingRange: [
      creditRatingRangeValues[0].value,
      creditRatingRangeValues[creditRatingRangeValues.length - 1].value,
    ],
    couponRange: [
      couponRangeSliderValues[0].value,
      couponRangeSliderValues[couponRangeSliderValues.length - 1].value,
    ],
    issueVolumeRange: [
      issueVolumeRangeSliderValues[0].value,
      issueVolumeRangeSliderValues[issueVolumeRangeSliderValues.length - 1].value,
    ],
    maturityDateFrom: null,
    maturityDateTo: null,
    currency: null,
    ...initialFilter,
  });

  useEffect(() => {
    setValues({ ...values, ...initialFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilter]);

  const [industries, setIndustries] = useState([]);

  useEffect(() => {
    setIndustries(flattenIndustries(industriesData));
  }, [industriesData]);

  const [seniorityTypes, setSeniorityTypes] = useState([]);

  const fetchSeniorityTypes = async () => {
    const seniorityTypesData = await fetchCoveredBoundsSeniorityRatings();
    const data = seniorityTypesData.data.map(item => ({ value: item, label: item })) || [];
    setSeniorityTypes(data);
  };

  useEffect(() => {
    if (coveredBond) {
      fetchSeniorityTypes();
    }
  }, [coveredBond]);

  const handleSelectWithSearchChange = name => value => {
    setValues({
      ...values,
      [name]: value,
    });
  };

  const handleSeniorityTypesChange = (event, types) => {
    setValues({
      ...values,
      seniorityTypes: types,
    });
  };

  const handleIndustriesChange = (event, sectors) => {
    setValues({
      ...values,
      industries: sectors,
    });
  };

  const handleDateChange = name => date => {
    setValues({
      ...values,
      [name]: date ? new Date(date) : null,
    });
  };

  const handleRangeSliderChange = name => (event, newValues) => {
    setValues({
      ...values,
      [name]: newValues,
    });
  };

  const handleSubmit = () => {
    const {
      ratingRange,
      couponRange,
      issueVolumeRange,
      currency,
      maturityDateFrom,
      maturityDateTo,
    } = values;

    const cleanedValues = {
      ...values,
      countries: values.countries.map(({ iso }) => iso),
      industrySectors: values.industries.map(({ code, industryCode }) => ({
        sector: code,
        industry: industryCode,
      })),
      coveredBond: coveredBond || undefined,
      seniorityTypes: coveredBond ? values.seniorityTypes.map(({ value }) => value) : undefined,
      ratingFrom: encodeURIComponent(creditRatingRangeValues[ratingRange[0] - 1].label),
      ratingTo: encodeURIComponent(creditRatingRangeValues[ratingRange[1] - 1].label),
      couponRangeFrom: couponRange[0],
      couponRangeTo: couponRange[1],
      issueVolumeFrom: issueVolumeRange[0],
      issueVolumeTo: issueVolumeRange[1],
      issueVolumeCurrency: currency,
      maturityDateFrom: maturityDateFrom && maturityDateFrom.toISOString(),
      maturityDateTo: maturityDateTo && maturityDateTo.toISOString(),
    };

    onSubmit(cleanedValues);
  };

  return (
    <Dialog fullWidth open={open}>
      <DialogTitleWithClose onClose={onClose}>{t('Apply filters')}</DialogTitleWithClose>
      <DialogContent>
        {coveredBond && (
          <Autocomplete
            multiple
            options={seniorityTypes}
            onChange={handleSeniorityTypesChange}
            getOptionLabel={option => option.label}
            value={values.seniorityTypes}
            renderInput={params => (
              <TextField {...params} label={t('Seniority Type')} variant="outlined" />
            )}
            classes={groupedAutocompleteClasses}
          />
        )}

        <GroupedSelect
          multiple
          options={coveredBond ? countries : regions.data}
          value={values.countries}
          label={t('Country')}
          onChange={handleSelectWithSearchChange('countries')}
          getOptionLabel={option => option.name}
          groupBy={option => option.continent}
          disabled={lockCountry}
        />
        <Autocomplete
          multiple
          options={industries}
          onChange={handleIndustriesChange}
          getOptionLabel={option => option.name}
          groupBy={option => option.industryName}
          value={values.industries}
          renderInput={params => (
            <TextField {...params} label={t('Industries')} variant="outlined" />
          )}
          classes={groupedAutocompleteClasses}
        />
        <SelectWithSearch
          label={t('Rating type')}
          options={ratingTypes}
          value={values.ratingType}
          onChange={handleSelectWithSearchChange('ratingType')}
        />
        <Box mt={2} mb={1}>
          <RangeSlider
            label={t('Instrument credit rating range')}
            options={creditRatingRangeValues}
            values={values.ratingRange}
            onChange={handleRangeSliderChange('ratingRange')}
          />
        </Box>
        {!coveredBond && (
          <Box mt={2} mb={1}>
            <RangeSlider
              label={t('Coupon range')}
              options={couponRangeSliderValues}
              values={values.couponRange}
              onChange={handleRangeSliderChange('couponRange')}
            />
          </Box>
        )}
        <Box mt={2} mb={1}>
          <RangeSlider
            label={t('Instrument volume')}
            options={issueVolumeRangeSliderValues}
            values={values.issueVolumeRange}
            onChange={handleRangeSliderChange('issueVolumeRange')}
          />
        </Box>
        <Box mb={1}>
          <Typography>{t('Maturity date')}</Typography>
        </Box>
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <DatePicker
              className={classes.datePicker}
              disableToolbar
              inputFormat="dd/MM/yyyy"
              renderInput={() => <TextField label="From" />}
              margin="normal"
              id="date-picker-inline"
              value={values.maturityDateFrom}
              onChange={handleDateChange('maturityDateFrom')}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
            />
          </Grid>
          <Grid item>
            <Typography>{t('to')}</Typography>
          </Grid>
          <Grid item>
            <DatePicker
              className={classes.datePicker}
              disableToolbar
              inputFormat="dd/MM/yyyy"
              margin="normal"
              id="date-picker-inline"
              renderInput={() => <TextField label="To" />}
              value={values.maturityDateTo}
              onChange={handleDateChange('maturityDateTo')}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
            />
          </Grid>
        </Grid>
        <SelectWithSearch
          label={t('Currency')}
          options={currenciesOptions}
          value={values.currency}
          onChange={handleSelectWithSearchChange('currency')}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          {t('Apply filters')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

InstrumentsFiltersModal.defaultProps = {
  searchQuery: null,
  initialFilter: {},
  coveredBond: false,
  countries: [],
  lockCountry: false,
};

InstrumentsFiltersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
  initialFilter: PropTypes.object,
  coveredBond: PropTypes.bool,
  countries: PropTypes.array,
  lockCountry: PropTypes.bool,
};

export default InstrumentsFiltersModal;
